/** Actual Budget stores amounts as integer cents. */
export function centsToDisplay(amountCents: number | undefined | null): number {
  if (amountCents == null || !Number.isFinite(amountCents)) {
    return 0;
  }
  return amountCents / 100;
}

/** Actual Budget stores spending as negative cents; display as positive spending. */
export function formatSpentAmount(
  amountCents: number | undefined | null,
  currency = 'USD'
): string {
  const value = amountCents ?? 0;
  const displayCents = value < 0 ? -value : value;
  return formatBudgetCurrency(displayCents, currency);
}

export function formatBudgetCurrency(
  amountCents: number | undefined | null,
  currency = 'USD'
): string {
  const value = centsToDisplay(amountCents);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
}

export function formatMonthLabel(month: string): string {
  const [year, monthNum] = month.split('-').map(Number);
  if (!year || !monthNum) return month;
  const date = new Date(year, monthNum - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [year, monthNum] = month.split('-').map(Number);
  const date = new Date(year, monthNum - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Sorted copy of YYYY-MM strings. */
export function sortMonths(months: string[]): string[] {
  return [...months].filter(m => /^\d{4}-\d{2}$/.test(m)).sort();
}

/**
 * Adjacent month within an available list (skips gaps).
 * delta: -1 previous, +1 next. Returns null at the boundary.
 */
export function adjacentAvailableMonth(
  months: string[],
  current: string,
  delta: -1 | 1
): string | null {
  const sorted = sortMonths(months);
  const idx = sorted.indexOf(current);
  if (idx === -1) {
    return null;
  }
  return sorted[idx + delta] ?? null;
}

export function canNavigateMonth(
  months: string[],
  current: string,
  delta: -1 | 1
): boolean {
  return adjacentAvailableMonth(months, current, delta) != null;
}
